// 情绪分析服务 - 分析对话内容的情感倾向
import { RealAIService } from './realAiService.js'
import settingsService from './settingsService.js'

class EmotionAnalysisService {
  /**
   * 分析消息的情感倾向
   * @param {string} message - 消息内容
   * @returns {string} 表情类型
   */
  async analyzeEmotion(message) {
    try {
      const languageConfig = settingsService.getLanguageModelConfig()
      
      if (!languageConfig.apiKey || !languageConfig.apiUrl) {
        return this.simpleEmotionAnalysis(message)
      }

      const prompt = `分析以下消息的情感倾向，返回一个表情类型：
- neutral: 中性、平静
- happy: 开心、高兴、愉快
- shy: 害羞、腼腆、不好意思
- angry: 生气、愤怒、不满
- surprised: 惊讶、震惊、意外
- sad: 难过、伤心、失落

消息内容：${message}

只返回表情类型（如：happy），不要其他内容：`

      const result = await RealAIService.callLanguageModel(prompt)
      const emotion = result.trim().toLowerCase()
      
      // 验证返回的表情类型
      const validEmotions = ['neutral', 'happy', 'shy', 'angry', 'surprised', 'sad']
      if (validEmotions.includes(emotion)) {
        return emotion
      }
      
      return this.simpleEmotionAnalysis(message)
    } catch (error) {
      console.error('❌ 情绪分析失败:', error)
      return this.simpleEmotionAnalysis(message)
    }
  }

  /**
   * 简单的情感分析（降级方案）
   */
  simpleEmotionAnalysis(message) {
    const messageLower = message.toLowerCase()
    
    // 开心关键词
    const happyKeywords = ['开心', '高兴', '快乐', '喜欢', '爱', '哈哈', '😊', '😄', '太好了', '棒', '好']
    if (happyKeywords.some(k => messageLower.includes(k))) {
      return 'happy'
    }
    
    // 害羞关键词
    const shyKeywords = ['害羞', '不好意思', '脸红', '腼腆', '😳', '///']
    if (shyKeywords.some(k => messageLower.includes(k))) {
      return 'shy'
    }
    
    // 生气关键词
    const angryKeywords = ['生气', '愤怒', '讨厌', '烦', '😠', '😡', '哼']
    if (angryKeywords.some(k => messageLower.includes(k))) {
      return 'angry'
    }
    
    // 惊讶关键词
    const surprisedKeywords = ['惊讶', '震惊', '意外', '什么', '真的', '😲', '！', '！？']
    if (surprisedKeywords.some(k => messageLower.includes(k))) {
      return 'surprised'
    }
    
    // 难过关键词
    const sadKeywords = ['难过', '伤心', '失落', '😢', '😭', '哭']
    if (sadKeywords.some(k => messageLower.includes(k))) {
      return 'sad'
    }
    
    return 'neutral'
  }

  /**
   * 分析对话历史的情感倾向
   * @param {Array} messages - 消息列表
   * @returns {string} 整体表情类型
   */
  async analyzeConversationEmotion(messages) {
    if (!messages || messages.length === 0) {
      return 'neutral'
    }

    // 分析最近几条消息
    const recentMessages = messages.slice(-5)
    const emotions = []
    
    for (const msg of recentMessages) {
      if (msg.sender === 'character') {
        const emotion = await this.analyzeEmotion(msg.content)
        emotions.push(emotion)
      }
    }
    
    if (emotions.length === 0) {
      return 'neutral'
    }
    
    // 返回最常见的表情
    const emotionCounts = {}
    emotions.forEach(emotion => {
      emotionCounts[emotion] = (emotionCounts[emotion] || 0) + 1
    })
    
    const mostCommonEmotion = Object.keys(emotionCounts).reduce((a, b) => 
      emotionCounts[a] > emotionCounts[b] ? a : b
    )
    
    return mostCommonEmotion
  }
}

export default new EmotionAnalysisService()

